<template>
  <div class="auth-layout">
    <!-- O painel da marca. O "lunedi" em teal sobre índigo escuro é o gesto que o `App.vue` já
         declara na barra e que lá não se vê, porque um `<span class="text-white">` o cobre. -->
    <section class="auth-brand">
      <div class="auth-brand-content">
        <h1 class="auth-logo">lunedi</h1>
        <!-- A primeira linha enumera, a segunda conclui — e é a conclusão que leva o negrito.
             Em duas chaves, e não uma com `<br>`, porque a quebra é da FRASE e não da largura da
             tela: cada idioma quebraria num lugar diferente se dependesse de onde o texto
             coubesse. -->
        <p class="auth-tagline">{{ $tt('auth-tagline') }}</p>
        <p class="auth-tagline auth-tagline-strong">{{ $tt('auth-tagline-detail') }}</p>
      </div>
    </section>

    <!-- O formulário, numa coluna estreita e centrada. Sem card: metade da tela já é a moldura,
         e uma caixa dentro de outra seria moldura de moldura. -->
    <section class="auth-form">
      <div class="auth-form-content">
        <h2 class="auth-title">{{ title }}</h2>
        <p v-if="subtitle" class="auth-subtitle">{{ subtitle }}</p>

        <!-- O caminho do provedor vem primeiro, e o "ou" só existe quando ele existe: é o que
             diz que são duas alternativas, e não duas etapas. Quem desenha o separador é esta
             casca, e não cada tela — senão a mesma régua acaba escrita duas vezes e com
             espaçamentos diferentes, que é o que já aconteceu com as seções do app. -->
        <template v-if="$slots.social">
          <div class="auth-social"><slot name="social"/></div>
          <div class="auth-or">{{ $tt('auth.or') }}</div>
        </template>

        <slot/>

        <div v-if="$slots.foot" class="auth-foot"><slot name="foot"/></div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'

/**
 * A porta do lunedi: painel de marca de um lado, formulário do outro.
 *
 * Dois consumidores desde o primeiro dia — entrar e se cadastrar —, que antes repetiam três
 * `v-row justify="center"` com um `v-col sm="8" md="5"` cada, sem card, sem marca e sem nada que
 * dissesse de quem é a tela.
 *
 * **Ocupa a janela inteira** porque a barra do aplicativo não é desenhada nas rotas públicas —
 * ver o `App.vue`. Sem isso haveria dois logos na mesma tela, e a porta de entrada continuaria
 * parecendo mais uma página de dentro.
 *
 * No telefone o painel vira uma faixa curta no alto: a marca continua, a altura não.
 */
@Component({name: 'ontos-auth-layout'})
class AuthLayout extends Vue {
  @Prop({type: String, required: true}) readonly title!: string
  @Prop({type: String, default: ''}) readonly subtitle!: string
}

export default toNative(AuthLayout)
</script>

<style scoped>
.auth-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
}

.auth-brand {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  /* indigo-darken-4, o mesmo da barra fora de ambiente: a cor que o app usa para dizer "você
     está no lunedi, e não dentro de um ambiente". Aqui ninguém está dentro de nada ainda.

     Em hexadecimal, e não por classe do Vuetify, porque é superfície desta tela e não valor de
     dado — a regra do projeto de nunca escrever cor em hexa vale para as cores que SIGNIFICAM
     alguma coisa num dado, e são escolhidas pela paleta. */
  background-color: #1A237E;
  padding: 48px;
}

.auth-brand-content {
  max-width: 360px;
}

/* Branco, que é o logo que o aplicativo mostra de verdade: na barra o `h1` é
   `text-teal-accent-3`, mas um `<span class="text-white">` por dentro cobre a cor — o teal está
   lá como intenção antiga e não se vê. A porta usa o logo que existe, não o que ficou no CSS. */
.auth-logo {
  color: #FFFFFF;
  font-size: 3rem;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
}

/* O slogan: discreto ao lado do logo, que é quem tem de pesar no painel. */
.auth-tagline {
  color: rgba(255, 255, 255, 0.72);
  font-size: 1rem;
  line-height: 1.5;
  margin-top: 16px;
  max-width: 28ch;
}

/* Entre as duas linhas, só o respiro de uma quebra — não é parágrafo novo. */
.auth-tagline + .auth-tagline {
  margin-top: 0;
}

.auth-tagline-strong {
  font-weight: 700;
}

.auth-form {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 48px;
}

/* Estreita de propósito: formulário de autenticação tem campos curtos, e uma linha de entrada
   com 700px de largura é desconfortável de ler e de preencher. */
.auth-form-content {
  max-width: 360px;
  width: 100%;
}

.auth-title {
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.2;
}

.auth-subtitle {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  margin-top: 4px;
}

.auth-social {
  margin-top: 24px;
}

/* "ou" entre duas linhas, no lugar de um divisor seco: a palavra é o que diz que os dois
   caminhos são alternativas. */
.auth-or {
  align-items: center;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  font-size: 0.75rem;
  gap: 12px;
  margin: 20px 0 4px;
  text-transform: uppercase;
}

.auth-or::before,
.auth-or::after {
  background-color: rgba(0, 0, 0, 0.12);
  content: '';
  flex: 1;
  height: 1px;
}

.auth-foot {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  margin-top: 24px;
}

/* 960px é o corte `md` do projeto — o mesmo do `$vuetify.display` e o mesmo que o casco usa
   para encolher os rótulos da barra. */
@media (max-width: 959px) {
  .auth-layout {
    grid-template-columns: 1fr;
    min-height: 100vh;
  }

  .auth-brand {
    align-items: flex-start;
    justify-content: center;
    padding: 32px 24px 24px;
  }

  .auth-brand-content {
    max-width: none;
  }

  .auth-logo {
    font-size: 2rem;
  }

  /* O slogan sai no telefone: ali a tela é do formulário, e o que importa é o campo estar acima
     do teclado. As duas linhas saem juntas — meio slogan não é slogan. */
  .auth-tagline {
    display: none;
  }

  .auth-form {
    align-items: flex-start;
    justify-content: center;
    padding: 32px 24px;
  }
}
</style>
